export type RichResultSitelink = {
  name: string;
  path: string;
};

export function normalizeSitelinkPath(path: string): string {
  const trimmed = path.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
}

export function parseSitelinkItems(raw: unknown): RichResultSitelink[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const items: RichResultSitelink[] = [];
  const seen = new Set<string>();
  for (const row of raw) {
    if (!row || typeof row !== 'object') continue;
    const rec = row as Record<string, unknown>;
    const name = String(rec.name ?? rec.label ?? '').trim();
    const path = normalizeSitelinkPath(String(rec.path ?? rec.href ?? rec.url ?? ''));
    if (!name || !path) continue;
    const key = `${name.toLowerCase()}|${path.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    items.push({ name, path });
  }
  return items.length > 0 ? items : undefined;
}

export function sitelinksFromTextarea(raw: string): RichResultSitelink[] {
  const items: RichResultSitelink[] = [];
  for (const line of raw.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const [name, path] = trimmed.split('|').map((part) => part.trim());
    const parsed = parseSitelinkItems([{ name, path }]);
    if (parsed?.[0]) items.push(parsed[0]);
  }
  return items;
}

export function sitelinksToTextarea(items: RichResultSitelink[]): string {
  return items.map((item) => `${item.name} | ${item.path}`).join('\n');
}

export function absoluteSiteAsset(baseUrl: string, imagePath?: string): string | undefined {
  const value = imagePath?.trim();
  if (!value) return undefined;
  if (/^https?:\/\//i.test(value)) return value;
  return `${baseUrl.replace(/\/$/, '')}${value.startsWith('/') ? value : `/${value}`}`;
}

export function youtubeVideoId(url: string): string | undefined {
  try {
    const parsed = new URL(url.trim());
    if (parsed.hostname === 'youtu.be') return parsed.pathname.replace(/^\//, '') || undefined;
    if (parsed.hostname.includes('youtube.com')) {
      return parsed.searchParams.get('v') || parsed.pathname.split('/').filter(Boolean).pop() || undefined;
    }
  } catch {
    return undefined;
  }
  return undefined;
}

export function isPlayableVideoUrl(url: string): boolean {
  try {
    const host = new URL(url.trim()).hostname.replace(/^www\./, '');
    return (
      host === 'youtube.com' || host === 'youtu.be' || host === 'vimeo.com' || host.endsWith('.youtube.com')
    );
  } catch {
    return false;
  }
}

export function buildWebsiteSearchNode(options: {
  baseUrl: string;
  siteName: string;
  enabled?: boolean;
}): Record<string, unknown> | undefined {
  if (options.enabled !== true) return undefined;
  const origin = options.baseUrl.replace(/\/$/, '');
  return {
    '@type': 'WebSite',
    '@id': `${origin}/#website`,
    name: options.siteName,
    url: `${origin}/`,
    inLanguage: 'en-IN',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${origin}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

export function buildSitelinkItemList(options: {
  pageUrl: string;
  baseUrl: string;
  items?: RichResultSitelink[];
  enabled?: boolean;
}): Record<string, unknown> | undefined {
  if (options.enabled !== true || !options.items?.length) return undefined;
  const origin = options.baseUrl.replace(/\/$/, '');
  return {
    '@type': 'ItemList',
    '@id': `${options.pageUrl}#sitelinks`,
    name: 'Popular pages',
    itemListElement: options.items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      url: /^https?:\/\//i.test(item.path) ? item.path : `${origin}${item.path}`,
    })),
  };
}

export function buildImageObject(url?: string): Record<string, unknown> | undefined {
  if (!url) return undefined;
  return { '@type': 'ImageObject', url };
}

export function buildVideoObject(options: {
  pageUrl: string;
  embedUrl?: string;
  name?: string;
  description?: string;
  thumbnailUrl?: string;
  uploadDate?: string;
}): Record<string, unknown> | undefined {
  const embedUrl = options.embedUrl?.trim();
  if (!embedUrl || !isPlayableVideoUrl(embedUrl)) return undefined;
  const videoId = youtubeVideoId(embedUrl);
  const thumbnailUrl =
    options.thumbnailUrl?.trim() ||
    (videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : undefined);
  return {
    '@type': 'VideoObject',
    '@id': `${options.pageUrl}#video`,
    name: options.name?.trim() || 'Service video',
    description: options.description?.trim() || options.name?.trim() || 'Service video',
    embedUrl,
    thumbnailUrl,
    ...(options.uploadDate?.trim() ? { uploadDate: options.uploadDate.trim() } : {}),
    publisher: { '@type': 'Organization', url: options.pageUrl },
  };
}
